import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "./AdmissionsPage.css";

/* Theme: navy #0C1F3F, gold #C5A44E, gold-light #D4B96A (from home page) */
const heroBg = "/image-fix/admission-page-imgs/out/girl-sitting-with-laptop-2-800.jpg";

const STEPS = [
  {
    num: 1,
    title: "Complete the Online Application",
    desc: "Submit your online application form along with the required documentation based on the programme you are applying for. Send scanned copies to",
    email: "admissions@eusbd.eu",
    primary: true,
    docs: [
      "Up-to-date CV / Resume",
      "Profile Photo (white bg)",
      "ID or Passport Copy",
      "Letter of Motivation",
      "Academic Records (postgraduate)",
      "IELTS / TOEFL (if applicable)",
    ],
  },
  {
    num: 2,
    title: "Receive Admission Letter",
    desc: "The admissions committee evaluates every application. Results are sent via email within 5 to 10 working days.",
  },
  {
    num: 3,
    title: "Return Acceptance Form",
    desc: "Complete the acceptance form and return it to us to confirm your place.",
  },
  {
    num: 4,
    title: "Receive Your Invoice",
    desc: "Upon receipt of your acceptance form, we promptly forward your invoice with full payment details.",
  },
  {
    num: 5,
    title: "Make Fees Payment",
    desc: "We provide multiple payment methods for your registration and course fees.",
  },
  {
    num: 6,
    title: "Receive LMS Login Details",
    desc: "After payment, you receive registration links and login credentials for our Learning Management System.",
  },
  {
    num: 7,
    title: "Begin Your Studies",
    desc: "All formalities complete. Welcome to ESBD! Your academic journey begins.",
  },
];

const FEATURES = [
  {
    icon: "clock",
    title: "Rolling Admissions",
    desc: "No fixed deadlines. Apply any time of the year for July or January intake.",
  },
  {
    icon: "check",
    title: "Fair & Transparent",
    desc: "Every application is evaluated fairly by our admissions committee.",
  },
  {
    icon: "heart",
    title: "Personalised Support",
    desc: "Dedicated admissions team guiding you from application to enrolment.",
  },
];

const HELP_CARDS = [
  {
    title: "Preparing Your Application",
    desc: "Our resources offer valuable insights into presenting your strengths, achievements, and aspirations to our admissions committee.",
    link: "/register-your-interest",
    linkText: "LEARN MORE",
  },
  {
    title: "Assistance Every Step",
    desc: "Our dedicated admissions team provides personalised assistance. Whether you have questions about requirements or need guidance, we are here to help.",
    link: "mailto:admissions@eusbd.eu",
    linkText: "CONTACT ADMISSIONS",
  },
  {
    title: "Join Our Community",
    desc: "By enrolling at ESBD, you are becoming part of a dynamic network that will shape your career and impact the world.",
    link: "/certified",
    linkText: "EXPLORE PROGRAMMES",
  },
];

const STATS = [
  { value: "100%", label: "ONLINE DELIVERY" },
  { value: "Rolling", label: "ADMISSIONS" },
  { value: "7", label: "SIMPLE STEPS" },
  { value: "Global", label: "STUDENT BODY" },
];

const Icons = {
  clock: (
    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  check: (
    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M9 12l2 2 4-4" />
      <circle cx="12" cy="12" r="10" />
    </svg>
  ),
  heart: (
    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
    </svg>
  ),
  arrow: (
    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <line x1="7" y1="17" x2="17" y2="7" />
      <polyline points="7 7 17 7 17 17" />
    </svg>
  ),
  arrowRight: (
    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  ),
};

function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("adm-visible");
          observer.unobserve(el);
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

function RevealSection({ children, className = "", delay = 0 }) {
  const ref = useReveal();
  return (
    <div
      ref={ref}
      className={`adm-reveal ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

const MA_APPLY_URL = "https://admissions.eusbd.eu/esbd-apply/MA-GBSL/";

export default function AdmissionsPage() {
  return (
    <div className="adm-page">
      {/* HERO */}
      <section className="adm-hero">
        <div className="adm-hero__bg">
          <img src={heroBg} alt="ESBD Students" />
        </div>
        <div className="adm-hero__overlay" />
        <div className="adm-hero__inner">
          <div className="adm-hero__text">
            <div className="adm-hero__label">
              <span className="adm-hero__label-line" />
              <span>ADMISSIONS</span>
            </div>
            <h1>
              Begin Your <em>Academic Journey</em> With Us
            </h1>
            <p>
              Our admissions process is designed to be simple, transparent, and
              supportive. We welcome students from all backgrounds and guide you
              through every step.
            </p>
            <div className="adm-hero__btns">
              <a href={MA_APPLY_URL} target="_blank" rel="noreferrer" className="adm-btn adm-btn--gold">
                Apply Now {Icons.arrow}
              </a>
              <a href="mailto:admissions@eusbd.eu" className="adm-btn adm-btn--outline">
                Request Info
              </a>
            </div>
          </div>

          <div className="adm-hero__stats">
            {STATS.map((s, i) => (
              <div className="adm-stat" key={i}>
                <div className="adm-stat__value">{s.value}</div>
                <div className="adm-stat__label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WELCOME */}
      <section className="adm-welcome">
        <div className="adm-welcome__inner">
          <RevealSection className="adm-welcome__text">
            <h2>
              Welcome to <span>ESBD</span> Admissions
            </h2>
            <p>
              At The European School of Business &amp; Diplomacy, we believe in
              nurturing the leaders of tomorrow through a comprehensive education
              experience that fosters diplomacy, innovation, and leadership
              skills. Our admissions process is designed to be accessible and
              supportive.
            </p>
            <p>
              We operate on a rolling admissions basis, meaning applications can
              be submitted throughout the year for enrolment in either our July
              or January intake.
            </p>
          </RevealSection>

          <div className="adm-features">
            {FEATURES.map((f, i) => (
              <RevealSection key={i} delay={i * 120} className="adm-feature">
                <div className="adm-feature__icon">{Icons[f.icon]}</div>
                <div className="adm-feature__content">
                  <h4>{f.title}</h4>
                  <p>{f.desc}</p>
                </div>
              </RevealSection>
            ))}
          </div>
        </div>
      </section>

      {/* ADMISSION PROCESS */}
      <section className="adm-process">
        <div className="adm-process__inner">
          <RevealSection className="adm-section-header">
            <h2>Admission Process</h2>
            <p>7 simple steps from application to enrolment</p>
          </RevealSection>

          <RevealSection className="adm-step-primary">
            <div className="adm-step-primary__left">
              <div className="adm-step__header">
                <span className="adm-step__num">1</span>
                <h3>{STEPS[0].title}</h3>
              </div>
              <p className="adm-step__desc">
                {STEPS[0].desc}{" "}
                <a href={`mailto:${STEPS[0].email}`}>{STEPS[0].email}</a>
              </p>
            </div>
            <div className="adm-doc-tags">
              {STEPS[0].docs.map((d, i) => (
                <span className="adm-doc-tag" key={i}>
                  {d}
                </span>
              ))}
            </div>
          </RevealSection>

          <div className="adm-steps-grid">
            {STEPS.slice(1).map((step, i) => (
              <RevealSection key={step.num} delay={i * 80} className="adm-step-card">
                <div className="adm-step__header">
                  <span className="adm-step__num">{step.num}</span>
                  <h3>{step.title}</h3>
                </div>
                <p className="adm-step__desc">{step.desc}</p>
              </RevealSection>
            ))}
          </div>
        </div>
      </section>

      {/* HELP CARDS */}
      <section className="adm-help">
        <div className="adm-help__inner">
          {HELP_CARDS.map((card, i) => (
            <RevealSection key={i} delay={i * 120} className="adm-help-card">
              <h3>{card.title}</h3>
              <p>{card.desc}</p>
              {card.link.startsWith("mailto:") ? (
                <a href={card.link} className="adm-help-link">
                  {card.linkText} {Icons.arrowRight}
                </a>
              ) : (
                <Link to={card.link} className="adm-help-link">
                  {card.linkText} {Icons.arrowRight}
                </Link>
              )}
            </RevealSection>
          ))}
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="adm-cta">
        <RevealSection>
          <h2>Ready to Take the First Step?</h2>
          <p>Apply today and join a community of future global leaders.</p>
          <a href={MA_APPLY_URL} target="_blank" rel="noreferrer" className="adm-btn adm-btn--gold">
            Apply Now
          </a>
        </RevealSection>
      </section>
    </div>
  );
}
